import { FC, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../../../components/Modal";
import Table from "../../../../components/Table";
import Text from "../../../../components/Text";
import { SessionType } from "../../../../types/enums/SessionType";
import { Timeslots } from "../../../../types/enums/Timeslots";
import { Session } from "../../../../types/Session";

import ModalHeader from "../../../../components/Modal/ModalHeader";
import { TradingSessionFormModalMode } from "../../../../components/ModalForm/TradingSessionFormModal";
import Stat from "../../../../components/Text/Stat";
import Tooltip from "../../../../components/Tooltip";
import { AuthContext } from "../../../../contexts/AuthContext";
import { isAdminRole } from "../../../../utils/graduation";
import { NoTradesSession } from "../../../../types/NoTradesSession";
import {
    getFormattedFrDate,
    isWithinSessionDeleteWindow,
    isWithinSessionEditWindow,
    parseInputDateToLocal,
} from "../../../../utils/date";
import styles from "./styles.module.scss";

type Props = {
    open: boolean;
    onClose: () => void;
    handleNoTradeOpen: (
        mode: TradingSessionFormModalMode,
        session?: NoTradesSession | undefined
    ) => void;
    handleConfirmOpen: (session: Session | NoTradesSession) => void;
    sessions: (Session | NoTradesSession)[];
};

const DayDetailsModal: FC<Props> = ({
    open,
    onClose,
    handleNoTradeOpen,
    handleConfirmOpen,
    sessions,
}) => {
    const { getIsOwnDashboard, hasPermission, user } = useContext(AuthContext)!;
    const navigate = useNavigate();

    const getType = (session: Session | NoTradesSession) => {
        if ("sessionType" in session) {
            return session.sessionType === "buy" ? SessionType.buy : SessionType.sell;
        } else {
            return <Text variant="body-bold">No trades</Text>;
        }
    };


    const getOptions = (session: Session | NoTradesSession) => {
        const options = [];

        const canEdit = () => {
            if (!session) return false;
            if ("Trade" in session) return false;
            if (session.isWeekValidated) return false;

            const parsedDate = parseInputDateToLocal(session.date);
            const isWithinEditWindow = parsedDate
                ? isWithinSessionEditWindow(parsedDate)
                : false;

            return (
                isAdminRole(user?.role) ||
                (getIsOwnDashboard() &&
                    isWithinEditWindow &&
                    hasPermission("trading-session", "update"))
            );
        };

        if (canEdit()) {
            options.push({
                label: "Editer",
                onClick: () =>
                    handleNoTradeOpen("edit", session as NoTradesSession),
            });
        }

        const canDelete =
            !("Trade" in session) &&
            !session.isWeekValidated &&
            (hasPermission("admin-trading-sessions", "delete") ||
                (getIsOwnDashboard() &&
                    hasPermission("trading-session", "delete") &&
                    isWithinSessionDeleteWindow(session)));

        if (canDelete) {
            options.push({
                label: "Supprimer",
                onClick: () => handleConfirmOpen(session),
            });
        }

        return options;
    };

    const formatedSessions = () => {
        return sessions.map((session) => {
            let sessionOnClick = {
                onClick: () => {
                    if (getIsOwnDashboard()) {
                        navigate(`/trades/session/${session.id}`);
                    } else {
                        navigate(
                            `/trades/session/${session.id}?userId=${session.userId}`
                        );
                    }
                },
            };

            let formattedSession: any = {
                ...session,
                timeslot: Timeslots[session.timeslot as keyof typeof Timeslots],
                tradesCount: "Trade" in session ? session.Trade.length : "-",
                type: getType(session),
                total:
                    "Trade" in session ? (
                        <Stat type="currency">{session.total}</Stat>
                    ) : (
                        "-"
                    ),
            };

            if ("Trade" in session) {
                formattedSession = { ...formattedSession, ...sessionOnClick };
            } else {
                let noTradeEdit = {
                    options: getOptions(session),
                    comment: session.comment ? (
                        <Tooltip
                            content={
                                <div className={styles.tooltipChild}>
                                    <Text variant="body">
                                        {session.comment}
                                    </Text>
                                </div>
                            }
                        >
                            <Text maxLength={24} variant="body-bold">
                                {session.comment}
                            </Text>
                        </Tooltip>
                    ) : (
                        "-"
                    ),
                };

                formattedSession = { ...formattedSession, ...noTradeEdit };
            }

            return formattedSession;
        });
    };

    const headerDateLabel = (() => {
        if (sessions.length === 0) {
            return "...";
        }

        const parsedDate = parseInputDateToLocal(sessions[0].date);
        return parsedDate ? getFormattedFrDate(parsedDate) : "...";
    })();

    return (
        <Modal open={open} onClose={onClose}>
            <div className={styles.modalContent}>
                    <ModalHeader
                    title={`Détails du ${headerDateLabel}`}
                    handleClose={onClose}
                />
                <Table
                    className={styles.table}
                    data={formatedSessions()}
                    rows={[
                        {
                            label: "Créneau horaire",
                            value: "timeslot",
                        },
                        {
                            label: "Nombre de trades",
                            value: "tradesCount",
                        },
                        {
                            label: "Type de session",
                            value: "type",
                        },
                        {
                            label: "Gain / Perte",
                            value: "total",
                        },
                        {
                            label: "Commentaire / détails",
                            value: "comment",
                        },
                    ]}
                />
            </div>
        </Modal>
    );
};

export default DayDetailsModal;

